import {TbLayoutSidebarLeftCollapse} from "react-icons/tb";
import {HiPlus, HiSearch} from "react-icons/hi";
import {AiFillPushpin} from "react-icons/ai";
import ChatHistorySection from "../ChatHistorySection";
import ChatHistorySkeletonLoader from "../ChatHistorySkeletonLoader";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useChatService} from "../../../services";
import {setIsChatbotSidebarCollapsed} from "../../../redux/slices/chat";

interface ExpandedSidebarProps {
  onCreateNewChatClick: () => void;
  onSearchChatClick: () => void;
  onChatClick: (chat_id: number) => void;
}

const ExpandedSidebar: React.FC<ExpandedSidebarProps> = ({
  onCreateNewChatClick,
  onSearchChatClick,
  onChatClick
}) => {
  const dispatch = useDispatch();
  const { getChatHistoryList } = useChatService();

  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const [showPinnedList, setShowPinnedList] = useState(false);

  const {
    isChatbotSidebarCollapsed
  } = useSelector((state?: any) => state.chatMessages);

  const {
    showBigChatBox,
    showChatbox,
    chatHistoryByDate,
    chatHistoryListLoading,
    pinnedChats,
    currentPage,
    hasMoreChats,
    isLoadingMore
  } = useSelector((state?: any) => state.chatMessages);

  const collapseChatbotSidebar = () => {
    dispatch(setIsChatbotSidebarCollapsed(true));
  }

  const togglePinnedList = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowPinnedList(prev => !prev);
  }

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMoreChats) return;

    try {
      await getChatHistoryList(null, {
        page: currentPage + 1,
        per_page: 20,
        append: true
      });
    } catch (error) {
      console.error('Error loading more chats:', error);
    }
  };

  const chatHistory = useMemo(() => {
    const today = chatHistoryByDate.today || [];
    const yesterday = chatHistoryByDate.yesterday || [];
    const older = chatHistoryByDate.older || [];

    const getLastLabel = () => {
      if (older.length > 0) return 'Older';
      if (yesterday.length > 0) return 'Yesterday';
      if (today.length > 0) return 'Today';
      return null;
    };

    return {
      today,
      yesterday,
      older,
      lastLabel: getLastLabel()
    };
  }, [chatHistoryByDate]);

  useEffect(() => {
    setShowPinnedList(false);
  }, [showChatbox, showBigChatBox, chatHistoryByDate, pinnedChats]);

  useEffect(() => {
    if (showPinnedList && listContainerRef.current) {
      setTimeout(() => {
        if (listContainerRef.current) {
          listContainerRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [showPinnedList]);

  return (
    <>
      <div className="p-3 rounded ">
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <h2 className="text-base sm:text-lg ml-2 mt-2 sm:mt-3 font-semibold text-gray-800 dark:text-white">
            History
          </h2>
          <button
            onClick={collapseChatbotSidebar}
            className="p-0.5 sm:p-1 mt-1 sm:mt-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white"
            title="Collapse sidebar"
            style={{ cursor: isChatbotSidebarCollapsed ? 'default' : 'w-resize' }}
          >
            <TbLayoutSidebarLeftCollapse className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="flex flex-col space-y-1 mt-4">
          <button
            onClick={onCreateNewChatClick}
            className="group flex items-center gap-2 py-1.5 px-1  rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors duration-150"
          >
            <HiPlus className="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
            <span className="text-sm font-medium whitespace-nowrap transition-opacity duration-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">
              New Chat
            </span>
          </button>

          <button
            onClick={onSearchChatClick}
            className="group flex items-center gap-2 py-2 px-1  rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors duration-150"
            title="Search through chat history"
          >
            <HiSearch className="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
            <span className="text-sm font-medium whitespace-nowrap transition-opacity duration-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">
              Search Chat
            </span>
          </button>

          {pinnedChats && pinnedChats.length > 0 && (
            <>
              <button
                onClick={togglePinnedList}
                className="group flex items-center gap-2 py-2 px-1  rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors duration-150"
                title="Pinned Chats"
              >
                <AiFillPushpin className="h-5 w-5 -rotate-90 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                <span className="text-sm font-medium whitespace-nowrap transition-opacity duration-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                  Pinned Chats
                </span>
              </button>

            </>
          )}
        </div>
      </div>

      <div ref={listContainerRef} className="flex-1 min-h-0 px-1 pt-0  overflow-y-auto space-y-0">
        {chatHistoryListLoading ? (
          <ChatHistorySkeletonLoader />
        ) : (
          <>
            {pinnedChats && pinnedChats.length > 0 && (
              <>
                <ChatHistorySection
                  label=""
                  collapsible={false}
                  items={pinnedChats}
                  onChatClick={onChatClick}
                  expandedProp={showPinnedList}
                />
                {showPinnedList && (
                  <div className="mx-1 my-2 h-px bg-gray-200 dark:bg-gray-700/70" />
                )}
              </>
            )}

            {chatHistory.today && chatHistory.today.length > 0 && (
              <ChatHistorySection
                label="Today"
                items={chatHistory.today}
                onChatClick={onChatClick}
                showLoadMore={hasMoreChats && chatHistory.lastLabel === 'Today'}
                isLoadingMore={isLoadingMore}
                onLoadMore={handleLoadMore}
              />
            )}

            {chatHistory.yesterday && chatHistory.yesterday.length > 0 && (
              <ChatHistorySection
                label="Yesterday"
                items={chatHistory.yesterday}
                onChatClick={onChatClick}
                showLoadMore={hasMoreChats && chatHistory.lastLabel === 'Yesterday'}
                isLoadingMore={isLoadingMore}
                onLoadMore={handleLoadMore}
              />
            )}

            {chatHistory.older && chatHistory.older.length > 0 && (
              <ChatHistorySection
                label="Older"
                items={chatHistory.older}
                onChatClick={onChatClick}
                showLoadMore={hasMoreChats && chatHistory.lastLabel === 'Older'}
                isLoadingMore={isLoadingMore}
                onLoadMore={handleLoadMore}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ExpandedSidebar;